import React, { useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Table } from '../../components/Table';
import { useFetchAlpacaFeeMonthlyReportQuery } from '../../redux/features/alpacaFee/alpacaFee.apiSlice';
import { AlpacaFeeMonthlyReportRow } from '../../types/alpacaFee';
import { round2Format } from '../../utils/number';

/** This month as `YYYY-MM` (the report is by New York month; near midnight NY the two can differ by a day). */
const currentMonth = (): string => new Date().toISOString().slice(0, 7);

const usd = (value: number) => `$${round2Format(value)}`;

const COLUMNS: GridColDef<AlpacaFeeMonthlyReportRow>[] = [
  { field: 'feeType', headerName: 'Fee', width: 210 },
  {
    field: 'source',
    headerName: 'Source',
    width: 120,
    renderCell: ({ value }) => (value === 'SHADOW' ? 'SHADOW (test, not taken)' : value),
  },
  { field: 'alpacaFeeShareRowCount', headerName: 'Rows', type: 'number', width: 90 },
  {
    field: 'feeAmountSum',
    headerName: 'Users paid',
    type: 'number',
    width: 140,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'alpacaPartSum',
    headerName: 'Alpaca bills us',
    type: 'number',
    width: 150,
    renderCell: ({ value }) => usd(value),
  },
  {
    field: 'platformPartSum',
    headerName: 'Ours',
    type: 'number',
    width: 130,
    renderCell: ({ value }: GridRenderCellParams<AlpacaFeeMonthlyReportRow>) => (
      <Box component="span" sx={{ color: value < 0 ? 'error.main' : 'inherit' }}>
        {usd(value)}
      </Box>
    ),
  },
  {
    field: 'updatedAt',
    headerName: 'Rebuilt',
    type: 'dateTime',
    flex: 1,
    minWidth: 170,
    valueGetter: ({ value }) => (value ? new Date(value) : null),
  },
];

/**
 * The Alpaca fees of one New York month, per fee type and source — for finance to compare with Alpaca's monthly
 * invoice. Rebuilt every morning for the current and the previous month.
 */
export function AlpacaFeeReport() {
  const [month, setMonth] = useState(currentMonth());
  const { data: rows = [], isFetching, isError } = useFetchAlpacaFeeMonthlyReportQuery(month, { skip: !month });

  return (
    <Container component="main" maxWidth="xl">
      <Box sx={{ pb: 3, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
        <Typography variant="h4">Alpaca Fee Report</Typography>
        <TextField
          id="alpaca-fee-report-month"
          type="month"
          label="Month (New York)"
          size="small"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rebuilt every morning for this month and last month. &quot;Ours&quot; is negative where we covered a cost (a
        paused fee, a wire under $100).
      </Typography>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the report.
        </Alert>
      )}

      {!isFetching && !isError && rows.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No fees for {month} yet.
        </Alert>
      )}

      <Table
        rows={rows}
        columns={COLUMNS}
        loading={isFetching}
        paginationMode="client"
        sortingMode="client"
        filterMode="client"
        initialSortModel={[{ field: 'feeType', sort: 'asc' }]}
        rowCount={rows.length}
        wrapperHeight={420}
      />
    </Container>
  );
}
